/**
 * OpenAI-specific utility functions and constants related to the realtime API.
 * For now, we define the relevant constants that we actually use since it's
 * fairly minimal, but we may want to pull in typedefs from a library in the future.
 *
 * Docs:
 * https://platform.openai.com/docs/api-reference/realtime-client-events
 * https://platform.openai.com/docs/api-reference/realtime-server-events
 */

export interface FunctionCallDoneEvent {
  type: 'conversation.item.done';
  item: {
    type: 'function_call';
    id?: string;
    name: string;
    call_id: string;
    arguments: string;
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isFunctionCallDoneEvent(event: any): event is FunctionCallDoneEvent {
  return event.type === 'conversation.item.done' && event.item.type === 'function_call';
}

/**
 * Create an event to append a tool response to the conversation.
 */
export function createToolResponseEvent(callId: string, output: string): unknown {
  return {
    type: 'conversation.item.create',
    item: {
      type: 'function_call_output',
      call_id: callId,
      output,
    },
  };
}

export interface RealtimeSessionTool {
  type: 'function';
  name: string;
  description: string;
  parameters: Record<string, unknown>;
}

export interface RealtimeSessionUpdate {
  type: 'realtime';
  instructions: string;
  tools: Array<RealtimeSessionTool>;
}

export interface SessionUpdateEvent {
  event_id: string;
  type: 'session.update';
  session: RealtimeSessionUpdate;
}

/** Replace the active session's instructions and server-authorized tool allowlist. */
export function createSessionUpdateEvent(
  session: RealtimeSessionUpdate,
  eventId: string
): SessionUpdateEvent {
  return {
    event_id: eventId,
    type: 'session.update',
    session,
  };
}

export interface SessionUpdatedEvent {
  event_id: string;
  type: 'session.updated';
}

export interface SessionCreatedEvent {
  type: 'session.created';
  session: {id: string};
}

/** Captures the provider session identifier emitted when the Realtime connection is established. */
export function isSessionCreatedEvent(event: unknown): event is SessionCreatedEvent {
  return (
    typeof event === 'object' &&
    event != null &&
    'type' in event &&
    event.type === 'session.created' &&
    'session' in event &&
    typeof event.session === 'object' &&
    event.session != null &&
    'id' in event.session &&
    typeof event.session.id === 'string'
  );
}

/** Matches the provider acknowledgement emitted after applying a session update. */
export function isSessionUpdatedEvent(event: unknown): event is SessionUpdatedEvent {
  return (
    typeof event === 'object' &&
    event != null &&
    'type' in event &&
    event.type === 'session.updated' &&
    'event_id' in event &&
    typeof event.event_id === 'string'
  );
}

/**
 * Create an event to start model inference. This must be triggered directly for
 * manually-posted events.
 */
const VOICE_RESPONSE_CREATE_EVENT_ID_METADATA_KEY = 'benchling_voice_response_create_event_id';

export interface TriggerInferenceEvent {
  event_id: string;
  type: 'response.create';
  response: {
    metadata: Record<string, string>;
  };
}

export interface CancelResponseEvent {
  event_id: string;
  type: 'response.cancel';
  response_id: string;
}

/** Cancel one correlated default-conversation response. */
export function createCancelResponseEvent(responseId: string, eventId: string): CancelResponseEvent {
  return {
    event_id: eventId,
    type: 'response.cancel',
    response_id: responseId,
  };
}

export function createTriggerInferenceEvent(eventId: string): TriggerInferenceEvent {
  return {
    event_id: eventId,
    type: 'response.create',
    response: {
      metadata: {
        [VOICE_RESPONSE_CREATE_EVENT_ID_METADATA_KEY]: eventId,
      },
    },
  };
}

/**
 * A prior conversation turn (BNCH-167097), serialized server-side into OpenAI Realtime
 * conversation-item shape. A user turn carries `input_text` content; an assistant turn carries
 * `output_text`. Keep this wire contract synchronized with VoiceConversationItem in
 * benchling/llm/data_capture_assistant/agent/realtime_voice.py.
 */
export interface VoiceConversationItem {
  id: string;
  type: 'message';
  role: 'user' | 'assistant';
  content: Array<{type: 'input_text' | 'output_text'; text: string}>;
}

export interface ConversationHistoryItemCreateEvent {
  previous_item_id?: string;
  event_id: string;
  type: 'conversation.item.create';
  item: VoiceConversationItem;
}

/**
 * Create an event to replay a prior conversation turn into the session (BNCH-167097). The OpenAI
 * session-create call has no way to seed history, so the client replays prior thread messages as
 * conversation.item.create events over the data channel after connecting -- mirroring the existing
 * createToolResponseEvent pattern.
 */
export function createConversationHistoryItemEvent(
  item: VoiceConversationItem,
  previousItemId?: string
): ConversationHistoryItemCreateEvent {
  return {
    ...(previousItemId === undefined ? {} : {previous_item_id: previousItemId}),
    event_id: `voice_history_replay_${item.id}`,
    type: 'conversation.item.create',
    item,
  };
}

// Realtime transcript events arrive independently and are correlated by OpenAI item ID.

export interface ConversationItemCreatedEvent {
  type: 'conversation.item.created';
  // Only user items need asynchronous transcription and its never-drop timeout.
  item: {id: string; role?: 'user' | 'assistant'};
  previous_item_id: string | null;
}

/** Captures turn ordering before its transcript completes. */
export function isConversationItemCreatedEvent(event: unknown): event is ConversationItemCreatedEvent {
  return (
    isConversationItemEvent(event, 'conversation.item.created') &&
    'previous_item_id' in event &&
    (event.previous_item_id == null || typeof event.previous_item_id === 'string')
  );
}

export interface ConversationItemAddedEvent {
  type: 'conversation.item.added';
  item: {id: string; role?: 'user' | 'assistant'};
  previous_item_id?: string | null;
}

/** GA counterpart to the beta conversation.item.created event. */
export function isConversationItemAddedEvent(event: unknown): event is ConversationItemAddedEvent {
  return (
    isConversationItemEvent(event, 'conversation.item.added') &&
    (!('previous_item_id' in event) ||
      event.previous_item_id == null ||
      typeof event.previous_item_id === 'string')
  );
}

function isConversationItemEvent(
  event: unknown,
  type: 'conversation.item.created' | 'conversation.item.added'
): event is {type: typeof type; item: {id: string}; previous_item_id?: unknown} {
  return (
    typeof event === 'object' &&
    event != null &&
    'type' in event &&
    event.type === type &&
    'item' in event &&
    typeof event.item === 'object' &&
    event.item != null &&
    'id' in event.item &&
    typeof event.item.id === 'string' &&
    (!('role' in event.item) || event.item.role === 'user' || event.item.role === 'assistant')
  );
}

export interface ConversationItemAcknowledgementEvent {
  type: 'conversation.item.created' | 'conversation.item.added' | 'conversation.item.done';
  item: {id: string};
}

/** Matches server acceptance/completion of a client-created conversation item. */
export function isConversationItemAcknowledgementEvent(
  event: unknown
): event is ConversationItemAcknowledgementEvent {
  return (
    typeof event === 'object' &&
    event != null &&
    'type' in event &&
    (event.type === 'conversation.item.created' ||
      event.type === 'conversation.item.added' ||
      event.type === 'conversation.item.done') &&
    'item' in event &&
    typeof event.item === 'object' &&
    event.item != null &&
    'id' in event.item &&
    typeof event.item.id === 'string'
  );
}

export interface RealtimeErrorEvent {
  type: 'error';
  error: {
    event_id?: string | null;
    message: string;
  };
}

/** Matches a Realtime provider error, with an optional client-event correlation ID. */
export function isRealtimeErrorEvent(event: unknown): event is RealtimeErrorEvent {
  return (
    typeof event === 'object' &&
    event != null &&
    'type' in event &&
    event.type === 'error' &&
    'error' in event &&
    typeof event.error === 'object' &&
    event.error != null &&
    'message' in event.error &&
    typeof event.error.message === 'string' &&
    (!('event_id' in event.error) || event.error.event_id == null || typeof event.error.event_id === 'string')
  );
}

export interface AssistantOutputItemAddedEvent {
  type: 'response.output_item.added';
  response_id: string;
  item: {
    id: string;
    type: 'message';
    role: 'assistant';
  };
}

/** Matches the first event that identifies an assistant message generated by a response. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isAssistantOutputItemAddedEvent(event: any): event is AssistantOutputItemAddedEvent {
  return (
    event.type === 'response.output_item.added' &&
    event.item?.type === 'message' &&
    event.item.role === 'assistant'
  );
}

export interface ResponseCreatedEvent {
  type: 'response.created';
  response: {
    id: string;
    metadata?: Record<string, unknown> | null;
  };
}

/** Matches the point at which the model starts preparing a response. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isResponseCreatedEvent(event: any): event is ResponseCreatedEvent {
  return event.type === 'response.created' && typeof event.response?.id === 'string';
}

/** Returns the client event ID carried through response metadata, when present. */
export function getResponseCreateClientEventId(event: ResponseCreatedEvent): string | null {
  const clientEventId = event.response.metadata?.[VOICE_RESPONSE_CREATE_EVENT_ID_METADATA_KEY];
  return typeof clientEventId === 'string' ? clientEventId : null;
}

export interface ResponseDoneEvent {
  type: 'response.done';
  response: {
    id: string;
    status: 'completed' | 'cancelled' | 'failed' | 'incomplete';
  };
}

/** Matches the terminal event for a generated response. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isResponseDoneEvent(event: any): event is ResponseDoneEvent {
  return event.type === 'response.done' && typeof event.response?.id === 'string';
}

export interface InputAudioBufferSpeechStartedEvent {
  type: 'input_audio_buffer.speech_started';
  item_id: string;
}

/** Matches server VAD speech start, before the user conversation item is committed. */
export function isInputAudioBufferSpeechStartedEvent(
  event: unknown
): event is InputAudioBufferSpeechStartedEvent {
  return (
    typeof event === 'object' &&
    event != null &&
    'type' in event &&
    event.type === 'input_audio_buffer.speech_started' &&
    'item_id' in event &&
    typeof event.item_id === 'string'
  );
}

export interface InputAudioBufferClearedEvent {
  type: 'input_audio_buffer.cleared';
}

export interface InputAudioBufferSpeechStoppedEvent {
  type: 'input_audio_buffer.speech_stopped';
  item_id: string;
}

/** Matches the end of a server VAD speech interval. */
export function isInputAudioBufferSpeechStoppedEvent(
  event: unknown
): event is InputAudioBufferSpeechStoppedEvent {
  return (
    typeof event === 'object' &&
    event != null &&
    'type' in event &&
    event.type === 'input_audio_buffer.speech_stopped' &&
    'item_id' in event &&
    typeof event.item_id === 'string'
  );
}

interface OutputAudioBufferEvent {
  response_id: string;
}

export interface OutputAudioBufferStartedEvent extends OutputAudioBufferEvent {
  type: 'output_audio_buffer.started';
}

export interface OutputAudioBufferStoppedEvent extends OutputAudioBufferEvent {
  type: 'output_audio_buffer.stopped';
}

export interface OutputAudioBufferClearedEvent extends OutputAudioBufferEvent {
  type: 'output_audio_buffer.cleared';
}

function isOutputAudioBufferEvent(event: unknown, type: string): event is OutputAudioBufferEvent {
  return (
    typeof event === 'object' &&
    event != null &&
    'type' in event &&
    event.type === type &&
    'response_id' in event &&
    typeof event.response_id === 'string'
  );
}

/** Matches the point at which WebRTC begins playing a response. */
export function isOutputAudioBufferStartedEvent(event: unknown): event is OutputAudioBufferStartedEvent {
  return isOutputAudioBufferEvent(event, 'output_audio_buffer.started');
}

/** Matches the point at which a response has fully drained from the playback buffer. */
export function isOutputAudioBufferStoppedEvent(event: unknown): event is OutputAudioBufferStoppedEvent {
  return isOutputAudioBufferEvent(event, 'output_audio_buffer.stopped');
}

/** Matches playback being cut off before the response buffer drains. */
export function isOutputAudioBufferClearedEvent(event: unknown): event is OutputAudioBufferClearedEvent {
  return isOutputAudioBufferEvent(event, 'output_audio_buffer.cleared');
}

/** Matches cancellation of uncommitted input audio. */
export function isInputAudioBufferClearedEvent(event: unknown): event is InputAudioBufferClearedEvent {
  return (
    typeof event === 'object' &&
    event != null &&
    'type' in event &&
    event.type === 'input_audio_buffer.cleared'
  );
}

export interface AssistantTranscriptDoneEvent {
  type: 'response.output_audio_transcript.done';
  item_id: string;
  transcript: string;
}

/** Matches the completed assistant transcript emitted by the Realtime model. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isAssistantTranscriptDoneEvent(event: any): event is AssistantTranscriptDoneEvent {
  return event.type === 'response.output_audio_transcript.done';
}

export interface UserTranscriptionCompletedEvent {
  type: 'conversation.item.input_audio_transcription.completed';
  item_id: string;
  transcript: string;
}

/** Matches asynchronous user transcription, correlated by item ID. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isUserTranscriptionCompletedEvent(event: any): event is UserTranscriptionCompletedEvent {
  return event.type === 'conversation.item.input_audio_transcription.completed';
}

export interface UserTranscriptionFailedEvent {
  type: 'conversation.item.input_audio_transcription.failed';
  item_id: string;
}

/** Matches transcription failure so the caller can persist a terminal fallback. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isUserTranscriptionFailedEvent(event: any): event is UserTranscriptionFailedEvent {
  return event.type === 'conversation.item.input_audio_transcription.failed';
}
